<script lang="ts" setup>
import { email as validEmail, required } from '@vee-validate/rules';
import { Field } from 'vee-validate';
import { computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { getConfigEntry } from '@/api/config';
import FieldErrorIcon from '@/components/base/assets/FieldErrorIcon.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import InlineError, { InputFailedEvent } from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import { useExpressCheckout } from '@/composables/useExpressCheckout';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { CheckoutEvents } from '@/utils/analytics';

export interface GuestEmailVModel {
  address: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: GuestEmailVModel;
    stepNumber?: number;
  }>(),
  {
    stepNumber: 2,
  },
);

const emit = defineEmits<{ 'update:modelValue': [GuestEmailVModel] }>();

const email = computed({
  get: () => props.modelValue.address,
  set: (val) => emit('update:modelValue', { ...props.modelValue, address: val }),
});

const { errorMessages, handleEmailTypos, validatorFailed, validatorPassed } = useForm();
const store = useStore();
const router = useRouter();
const { offerExpressCheckout, requestShopPayPayment } = useExpressCheckout(
  store,
  router,
  () => 'Checkout',
);
const {
  acceptSuggestion,
  animationClasses: mailcheckAnimationUtilities,
  checkForSuggestions,
  removeSuggestion,
  suggestedEmail,
} = handleEmailTypos(email);

const onInputBlur = (passed: boolean, event: FocusEvent) => {
  if (passed) {
    CheckoutEvents.validInput({ stepNumber: props.stepNumber, field: 'Guest Email' });
  }
  if ((event.relatedTarget as HTMLElement | null)?.id !== 'emailSuggestion') {
    removeSuggestion();
  }
};

const onInputFailed = ({ field, error }: InputFailedEvent) => {
  CheckoutEvents.validation({ stepNumber: props.stepNumber, field, error });
};

watch(email, () => {
  checkForSuggestions();
});

const mailcheckBaseUtilities = [
  'bg-black',
  'leading-3',
  'px-4',
  'py-3',
  'text-sm',
  'text-white',
].join(' ');

const emailValidator: FlexibleMessageValidator<string> = (value) => {
  if (!required(value)) return errorMessages.email.required;
  if (!validEmail(value)) return errorMessages.email.email;
  return true;
};

onMounted(async () => {
  if (offerExpressCheckout.value) {
    const shopifyConfig = getConfigEntry('shopify');
    window.ShopPay?.PaymentRequest.configure({
      shopId: shopifyConfig.shopId,
      clientId: shopifyConfig.clientId,
    });

    window.ShopPay?.PaymentRequest.createLogin({ emailInputId: 'email' }).render(
      '#shop-pay-login-container',
    );
    requestShopPayPayment();
  }
});
</script>

<template>
  <div>
    <Field v-model="email" name="email" :rules="emailValidator" v-slot="{ errors, field, meta }">
      <div id="shop-pay-login-container" class="relative z-10 w-full">
        <Input
          v-bind="field"
          type="email"
          id="email"
          class="relative z-20"
          placeholder="email"
          autofocus
          autocomplete="username"
          ref="emailInput"
          :invalid="validatorFailed(meta)"
          @blur="onInputBlur(validatorPassed(meta), $event)"
          data-test="recipient-email"
          required
        />
        <FloatingLabel
          for="email"
          class="left-0 z-20"
          :invalid="validatorFailed(meta)"
          data-test="email-label"
        >
          Email*
        </FloatingLabel>
        <div
          id="emailSuggestion"
          class="absolute top-0 z-10 w-full cursor-pointer underline-source"
          :class="[mailcheckBaseUtilities, mailcheckAnimationUtilities]"
          data-test="mailcheck-suggestion"
          :tabindex="suggestedEmail ? 0 : -1"
          @click="acceptSuggestion($refs.emailInput.$el)"
          @keydown.tab.exact="removeSuggestion"
          @keyup.enter="acceptSuggestion($refs.emailInput.$el)"
          :aria-hidden="!suggestedEmail"
        >
          Did you mean <span class="underline-target">{{ suggestedEmail }}</span
          >?
        </div>
        <FieldErrorIcon class="z-20" :showErrorIcon="validatorFailed(meta)" />
        <InlineError
          :errors="errors"
          fieldName="Guest Email"
          :invalid="validatorFailed(meta)"
          data-test="guest-email-validation-error"
          @input-failed="onInputFailed"
        />
      </div>
    </Field>
  </div>
</template>
